import { Container } from 'pixi.js';

import { SequenceElement } from './SequenceElement';
import { Tween } from './Tween';

type TargetType = { [key: string]: any };
type PropsType = { [key: string]: number };

export class Sequence {
  private readonly seqElements: SequenceElement[] = [];

  /**
   * Append tween.
   * @param tween Tween
   */
  public append(tween: Tween): Sequence {
    const elm = new SequenceElement();
    elm.tweens.push(tween);
    this.seqElements.push(elm);
    return this;
  }

  /**
   * Syntax sugar of append function.
   * (append) seq.append(new Tween(obj, property, duration)) );
   * (appendTo) seq.appendTo(obj, property, duration);
   * @param target Target
   * @param props Properties
   * @param duration Duration
   */
  public appendTo(target: TargetType, props: PropsType, duration: number): Sequence {
    return this.append(new Tween(target, props, duration));
  }

  /**
   * Append callback.
   * @param callback Callback
   */
  public appendCallback(callback: () => void): Sequence {
    if (this.seqElements.length === 0) {
      // add dummy data
      this.append(new Tween(new Container(), { x: 0 }, 0));
    }
    this.seqElements[this.seqElements.length - 1].callback = callback;
    return this;
  }

  /**
   * Join tween.
   * @param tween Tween.
   */
  public join(tween: Tween): Sequence {
    if (this.seqElements.length === 0) this.seqElements[0] = new SequenceElement();
    this.seqElements[this.seqElements.length - 1].tweens.push(tween);
    return this;
  }

  /**
   * Syntax sugar of join function.
   * (join) seq.join(new Tween(obj, property, duration)) );
   * (joinTo) seq.joinTo(obj, property, duration);
   * @param target Target
   * @param props Properties
   * @param duration Duration
   */
  public joinTo(target: TargetType, props: PropsType, duration: number): Sequence {
    return this.join(new Tween(target, props, duration));
  }

  /**
   * Start sequence.
   */
  public start(): void {
    for (let i = 0; i < this.seqElements.length; i++) {
      this.seqElements[i].longestDurationTween().chain(() => {
        if (this.seqElements[i].callback) {
          this.seqElements[i].callback();
        }
        if (i < this.seqElements.length - 1) {
          this.seqElements[i + 1].start();
        }
      });
    }
    this.seqElements[0].start();
  }

  /**
   * Stop sequence.
   */
  public stop(): void {
    for (const seqElement of this.seqElements) {
      seqElement.stop();
    }
  }

  /**
   * Pause sequence.
   */
  public pause(): void {
    for (const seqElement of this.seqElements) {
      seqElement.pause();
    }
  }

  /**
   * Resume sequence.
   */
  public resume(): void {
    for (const seqElement of this.seqElements) {
      seqElement.resume();
    }
  }

  /**
   * Wait for sequence completion.
   */
  public async waitForCompletion(): Promise<void> {
    const last = this.seqElements[this.seqElements.length - 1];
    await last.waitForCompletion();
  }
}
